(() => {
  const MOBILE_BREAKPOINT = 900;
  const SLIDE_INTERVAL_MS = 5000;
  const SLIDE_TRANSITION = "transform 0.5s ease-in-out";
  const SLIDE_TRANSITION_MS = 500;
  const SLIDE_RESET_OFFSET_MS = 10;
  const SWIPE_THRESHOLD_PX = 30;
  const HIDE_DURATION_MS = 24 * 60 * 60 * 1000;
  const HIDDEN_STORAGE_KEY = "wt-announcement-hidden";

  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.announcementBar = null;
      this.closeButton = null;
      this.announcementInterval = null;

      this.changeAnnouncement = this.changeAnnouncement.bind(this);
      this.previousAnnouncement = this.previousAnnouncement.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.hideForOneDay = this.hideForOneDay.bind(this);
      this.restartInterval = this.restartInterval.bind(this);
      this.isMobileView = this.isMobileView.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.pauseInterval = this.pauseInterval.bind(this);
    }

    // Read live so the element follows the attribute if it changes after upgrade.
    get mobileLayout() {
      return this.dataset.mobileLayout || "slider";
    }

    connectedCallback() {
      // Children are only guaranteed here, not in the constructor.
      this.announcementBar = this.querySelector("#wt-announcement__container");
      this.closeButton = this.querySelector(".wt-announcement__close");

      if (this.isSliderEnabled() && this.announcementBar) {
        this.init();
      }

      if (this.closeButton) {
        this.closeButton.addEventListener("click", this.hideForOneDay);
      }

      window.addEventListener("resize", this.handleResize);
    }

    init() {
      this.announcementInterval = setInterval(
        this.changeAnnouncement,
        SLIDE_INTERVAL_MS,
      );

      this.announcementBar.style.transition = SLIDE_TRANSITION;

      this.announcementBar.addEventListener(
        "touchstart",
        this.handleTouchStart,
        { passive: true },
      );
      this.announcementBar.addEventListener("touchmove", this.handleTouchMove, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchstart", this.pauseInterval, {
        passive: true,
      });
      this.announcementBar.addEventListener("touchend", this.restartInterval, {
        passive: true,
      });
    }

    isSliderEnabled() {
      return (
        this.mobileLayout === "slider" &&
        !this.announcementBar?.classList.contains(
          "wt-announcement__container--marquee",
        ) &&
        // A location selector is a control, not a slide: it lays out as its own wrapped row and
        // would be dragged off-screen with the announcements.
        !this.announcementBar?.querySelector(".wt-announcement__location")
      );
    }

    handleResize() {
      if (window.innerWidth >= MOBILE_BREAKPOINT && this.announcementBar) {
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
      }
    }

    isMobileView() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }

    pauseInterval() {
      clearInterval(this.announcementInterval);
    }

    restartInterval() {
      clearInterval(this.announcementInterval);
      this.announcementInterval = setInterval(
        this.changeAnnouncement,
        SLIDE_INTERVAL_MS,
      );
    }

    hideForOneDay() {
      const hiddenUntil = new Date().getTime() + HIDE_DURATION_MS;
      localStorage.setItem(HIDDEN_STORAGE_KEY, hiddenUntil);
      this.classList.add("_hidden");
    }

    changeAnnouncement() {
      if (!this.isSliderEnabled() || !this.isMobileView()) {
        return;
      }

      const totalSlides = this.announcementBar?.children.length;

      // Move to the next slide
      this.currentIndex = (this.currentIndex + 1) % totalSlides;
      const newPosition = -(this.currentIndex * 100);

      if (this.announcementBar) {
        this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
      }

      if (this.currentIndex === 0 && this.announcementBar) {
        // When we're back at the start, reset position with transition.
        setTimeout(() => {
          this.announcementBar.style.transition = "none";
          this.announcementBar.style.transform = "translateX(0)";
          this.currentIndex = 0;
          // Restore transition after resetting position
          setTimeout(() => {
            this.announcementBar.style.transition = SLIDE_TRANSITION;
          }, 0);
          // Just before the transition ends, so it happens before the next
          // slide movement.
        }, SLIDE_TRANSITION_MS - SLIDE_RESET_OFFSET_MS);
      }
    }

    previousAnnouncement() {
      if (!this.isSliderEnabled() || !this.isMobileView()) {
        return;
      }

      const totalSlides = this.announcementBar?.children.length;

      this.currentIndex = (this.currentIndex - 1 + totalSlides) % totalSlides;
      const newPosition = -(this.currentIndex * 100);
      if (this.announcementBar) {
        this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
      }
    }

    handleTouchStart(event) {
      if (this.isMobileView()) {
        this.touchStartX = event.touches[0].clientX;
      }
    }

    handleTouchMove(event) {
      if (this.isMobileView()) {
        this.touchEndX = event.touches[0].clientX;
      }
    }

    handleTouchEnd() {
      if (Math.abs(this.touchEndX - this.touchStartX) > SWIPE_THRESHOLD_PX) {
        if (this.touchEndX < this.touchStartX) {
          this.changeAnnouncement();
        } else {
          this.previousAnnouncement();
        }
      }
    }

    disconnectedCallback() {
      clearInterval(this.announcementInterval);

      if (this.announcementBar) {
        this.announcementBar.removeEventListener(
          "touchstart",
          this.handleTouchStart,
        );
        this.announcementBar.removeEventListener(
          "touchmove",
          this.handleTouchMove,
        );
        this.announcementBar.removeEventListener(
          "touchend",
          this.handleTouchEnd,
        );
        this.announcementBar.removeEventListener(
          "touchstart",
          this.pauseInterval,
        );
        this.announcementBar.removeEventListener(
          "touchend",
          this.restartInterval,
        );
      }

      if (this.closeButton) {
        this.closeButton.removeEventListener("click", this.hideForOneDay);
      }

      window.removeEventListener("resize", this.handleResize);
    }
  }

  if (!customElements.get("announcement-bar")) {
    customElements.define("announcement-bar", AnnouncementBar);
  }
})();
